/**
 * Maps filename matches returned by `/real-estate/sub-project-by-filename` to the
 * solution screen that should be opened for the document.
 *
 * The lookup itself is global (every solution the user can access), so a document
 * that was already run under one solution opens on that solution — not on whatever
 * solution happened to be open the last time the task pane was used.
 */
import type { SolutionId } from '../ui/components/SolutionsGrid';

export interface FilenameMatch {
  project_id: string;
  sub_project_id: string;
  project_name: string;
  sub_project_name: string;
  domain?: string | null;
  sub_domain?: string | null;
}

/** Backend `Project.sub_domain` values, lower-cased, per solution. */
const SUB_DOMAIN_TO_SOLUTION: Record<string, SolutionId> = {
  'master cross checks': 'master_cross_checks',
  'construction loan agreement': 'construction_loan',
};

export function solutionForMatch(match: FilenameMatch): SolutionId | null {
  const key = (match.sub_domain ?? '').trim().toLowerCase();
  return SUB_DOMAIN_TO_SOLUTION[key] ?? null;
}

/**
 * Matches that belong to one solution.
 *
 * A match with no `sub_domain` comes from a backend that predates solution
 * tagging; it is kept rather than dropped, so an add-in running against an older
 * backend still behaves the way it did before (the user picks the solution, the
 * per-solution lookup then works unchanged).
 */
export function matchesForSolution(
  matches: readonly FilenameMatch[] | null | undefined,
  solution: SolutionId,
): FilenameMatch[] {
  return (matches ?? []).filter((m) => {
    if (!(m.sub_domain ?? '').trim()) return true;
    return solutionForMatch(m) === solution;
  });
}

export type DocumentSolutionResolution =
  /** Nothing in the workspace carries this filename — show the solutions grid. */
  | { kind: 'none' }
  /** Exactly one client project owns the document — open it straight away. */
  | { kind: 'single'; solution: SolutionId; match: FilenameMatch }
  /**
   * Several client projects carry the document — ask which one.
   * `solution` is set only when all candidates share one solution.
   */
  | { kind: 'choose'; solution: SolutionId | null; matches: FilenameMatch[] };

/**
 * Decide what the task pane should show for the open document.
 *
 * Only matches belonging to a solution the add-in can open are considered; a
 * document that lives solely in an unsupported solution resolves to `none`.
 */
export function resolveDocumentSolution(
  matches: readonly FilenameMatch[] | null | undefined,
): DocumentSolutionResolution {
  const known = (matches ?? []).filter((m) => solutionForMatch(m) !== null);
  if (known.length === 0) return { kind: 'none' };
  if (known.length === 1) {
    return { kind: 'single', solution: solutionForMatch(known[0])!, match: known[0] };
  }
  const solutions = new Set(known.map((m) => solutionForMatch(m)!));
  return {
    kind: 'choose',
    solution: solutions.size === 1 ? known[0] && solutionForMatch(known[0])! : null,
    matches: known,
  };
}
